import React, { useState } from 'react';
import { Zap, Calendar, Flame, MapPin, ExternalLink, Key, Ticket } from 'lucide-react';
import { useCityPulse, PulseEvent, BusyZone } from '../hooks/useCityPulse';
import TicketModal from './TicketModal';

interface Props {
  userLocation?: { lat: number; lng: number } | null;
  onEventClick?: (lat: number, lon: number, name?: string) => void;
}

function busyColor(score: number) {
  if (score >= 80) return '#ff2244';
  if (score >= 55) return '#ff7700';
  if (score >= 30) return '#ffcc00';
  return '#00e676';
}

function busyLabel(score: number) {
  if (score >= 80) return 'PACKED';
  if (score >= 55) return 'BUSY';
  if (score >= 30) return 'ACTIVE';
  return 'QUIET';
}

function formatTime(ts: number) {
  return new Date(ts * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDate(ts: number) {
  const d = new Date(ts * 1000);
  const today = new Date();
  const diff = Math.floor((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return `Today ${formatTime(ts)}`;
  if (diff === 1) return `Tomorrow ${formatTime(ts)}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + formatTime(ts);
}

function startsInLabel(min: number | null) {
  if (min === null) return '';
  if (min < 60) return `starts in ${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `starts in ${h}h ${m}m` : `starts in ${h}h`;
}

const EventCard: React.FC<{ event: PulseEvent; onClick?: () => void; onBuyTickets?: () => void }> = ({ event, onClick, onBuyTickets }) => {
  const tmEventId = event.id.startsWith('tm_') ? event.id.slice(3) : null;

  return (
    <div
      className="border-b border-amber-950/50 px-3 py-2 hover:bg-amber-950/20 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        {event.isHappening
          ? <span className="mt-0.5 shrink-0 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          : <span className="mt-0.5 shrink-0 w-2 h-2 rounded-full bg-amber-700" />
        }
        <div className="min-w-0 flex-1">
          <div className="text-amber-400 text-xs font-bold truncate">{event.name}</div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-amber-700 text-xs flex items-center gap-1">
              <MapPin size={9} />{event.venue}
            </span>
            <span className="text-amber-800 text-xs">
              {event.isHappening ? 'NOW' : formatDate(event.start_ts)}
            </span>
            {event.startsIn !== null && (
              <span className="text-amber-600 text-xs">{startsInLabel(event.startsIn)}</span>
            )}
          </div>
          {event.category && (
            <span className="text-amber-900 text-xs uppercase tracking-wider">{event.category}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
          {tmEventId && event.ticket_url && (
            <button
              onClick={onBuyTickets}
              className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/40 transition-colors rounded-sm text-xs font-bold font-mono uppercase tracking-wider"
            >
              <Ticket size={10} /> BUY
            </button>
          )}
          {event.ticket_url && (
            <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" className="text-amber-800 hover:text-amber-500 transition-colors">
              <ExternalLink size={11} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default function CityPulse({ userLocation, onEventClick }: Props) {
  const { happening, upcoming, busyZones, hasApiKeys, loading } = useCityPulse(userLocation);
  const [tab, setTab] = useState<'now' | 'soon' | 'busy'>('now');
  const [ticketEvent, setTicketEvent] = useState<PulseEvent | null>(null);

  const topBusy = [...busyZones].sort((a, b) => b.score - a.score).slice(0, 5);

  return (
    <div className="flex flex-col h-full bg-black text-xs font-mono">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-900/40">
        <div className="flex items-center gap-2">
          <Zap size={13} className="text-amber-500" />
          <span className="font-orbitron text-xs font-bold text-amber-500 tracking-widest">CITY PULSE</span>
          {loading && <span className="text-amber-800 animate-pulse text-xs">LOADING…</span>}
        </div>
        {!hasApiKeys && (
          <span className="text-amber-900 flex items-center gap-1 text-xs" title="Add TICKETMASTER_API_KEY or EVENTBRITE_TOKEN to .env for live events">
            <Key size={10} /> no event keys
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-amber-900/40">
        {([['now', 'NOW', happening.length], ['soon', 'UPCOMING', upcoming.length], ['busy', 'BUSY SPOTS', topBusy.length]] as const).map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="flex-1 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors"
            style={{
              color: tab === key ? '#d4a843' : '#4a3500',
              borderBottom: tab === key ? '2px solid #d4a843' : '2px solid transparent',
              backgroundColor: tab === key ? 'rgba(212,168,67,0.06)' : 'transparent',
            }}
          >
            {label}
            {count > 0 && <span className="ml-1 text-amber-800">({count})</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0">

        {/* NOW — happening right now */}
        {tab === 'now' && (
          <>
            {happening.length === 0 && (
              <div className="px-3 py-6 text-center text-amber-900">
                {hasApiKeys ? 'No events happening right now.' : (
                  <div className="space-y-2">
                    <div>No event API keys configured.</div>
                    <div className="text-amber-950">Add <code className="text-amber-800">TICKETMASTER_API_KEY</code> to .env</div>
                    <div className="text-amber-950">Free at developer.ticketmaster.com</div>
                  </div>
                )}
              </div>
            )}
            {happening.map(e => (
              <EventCard
                key={e.id}
                event={e}
                onClick={() => e.lat && e.lon && onEventClick?.(e.lat, e.lon, e.venue)}
                onBuyTickets={() => { setTicketEvent(e); e.lat && e.lon && onEventClick?.(e.lat, e.lon, e.venue); }}
              />
            ))}
          </>
        )}

        {/* UPCOMING */}
        {tab === 'soon' && (
          <>
            {upcoming.length === 0 && (
              <div className="px-3 py-6 text-center text-amber-900">
                {hasApiKeys ? 'No upcoming events found.' : 'Add event API keys to see upcoming events.'}
              </div>
            )}
            {upcoming.map(e => (
              <EventCard
                key={e.id}
                event={e}
                onClick={() => e.lat && e.lon && onEventClick?.(e.lat, e.lon, e.venue)}
                onBuyTickets={() => { setTicketEvent(e); e.lat && e.lon && onEventClick?.(e.lat, e.lon, e.venue); }}
              />
            ))}
          </>
        )}

        {/* BUSY ZONES */}
        {tab === 'busy' && (
          <div className="p-3 space-y-2">
            <div className="text-amber-800 mb-3">Activity right now across Tucson:</div>
            {topBusy.map((z, i) => (
              <div key={z.name} className="flex items-center gap-3">
                <span className="text-amber-700 w-4 shrink-0">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold"
                      style={{ color: busyColor(z.score) }}
                    >
                      {busyLabel(z.score)}
                    </span>
                    <span className="text-amber-600 truncate">{z.name}</span>
                  </div>
                  {z.reason && (
                    <div className="text-amber-900 truncate">{z.reason}</div>
                  )}
                </div>
                {/* Activity bar */}
                <div className="w-16 bg-amber-950/40 rounded-full h-1.5 shrink-0">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${z.score}%`, backgroundColor: busyColor(z.score) }}
                  />
                </div>
              </div>
            ))}

            <div className="mt-4 pt-3 border-t border-amber-950 text-amber-900">
              Activity model based on time of day + live events. Updates every 5 min.
            </div>
          </div>
        )}
      </div>

      {/* Ticket purchase modal */}
      {ticketEvent && ticketEvent.ticket_url && (
        <TicketModal
          eventId={ticketEvent.id.startsWith('tm_') ? ticketEvent.id.slice(3) : ticketEvent.id}
          eventName={ticketEvent.name}
          venueName={ticketEvent.venue}
          ticketUrl={ticketEvent.ticket_url}
          onClose={() => setTicketEvent(null)}
        />
      )}
    </div>
  );
}
